import { afterNextRender, Component, computed, ElementRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TabItem, UiEmptyStateComponent, UIIconComponent, UiLoadingComponent, UiTabComponent } from '@design-system';
import { SwipeRightLeftDirective } from '@tools';
import { tap } from 'rxjs';
import { PortfolioAsset } from '../../models/portfolio.model';
import {
	getSortDirectionLabels,
	PORTFOLIO_SORT_CRITERION_LABELS,
	sortPortfolioAssets
} from '../../models/portfolio-sort.utils';
import { getTotalAssets } from '../../models/portfolio.utils';
import { PortfolioSortCriterion, PortfolioSortDirection } from '../../models/user-setting.model';
import { MASKED_VALUE } from './data-access/portfolio-view-state.service';
import { WalletStore } from '../wallet/data-access/wallet.store';
import { mapMarketViewTickToClosingPrice } from '../market-depth-best-limit/models/market-depth.model';
import { MarketViewService } from '../market/services/market-view.service';
import { PortfolioCardItemComponent } from './components/portfolio-card-item/portfolio-card-item.component';
import { PortfolioCompositionComponent } from './components/portfolio-composition/portfolio-composition.component';
import { PortfolioCompositionItem } from './components/portfolio-composition/portfolio-composition.models';
import { PortfolioMenuBottomSheetComponent } from './components/portfolio-menu-bottom-sheet/portfolio-menu-bottom-sheet.component';
import { PortfolioRiskStatementBottomSheetComponent } from './components/portfolio-risk-statement-bottom-sheet/portfolio-risk-statement-bottom-sheet.component';
import { PortfolioSettingsBottomSheetComponent } from './components/portfolio-settings-bottom-sheet/portfolio-settings-bottom-sheet.component';
import { PortfolioSortBottomSheetComponent } from './components/portfolio-sort-bottom-sheet/portfolio-sort-bottom-sheet.component';
import { PortfolioSummaryComponent } from './components/portfolio-summary/portfolio-summary.component';
import { PortfolioStore } from './data-access/portfolio.store';
import { PortfolioViewStateService } from './data-access/portfolio-view-state.service';
import { PortfolioHeaderEventConsumerService } from './events/portfolio-header-event-consumer.service';
import { PortfolioHeaderEventProducerService } from './events/portfolio-header-event-producer.service';
import { PORTFOLIO_MENU_EVENT_ID } from './events/portfolio-header-events.model';
import { IconNames } from '@icon/assets';

/**
 * The two figures the column header names, and which criterion lights each one
 * up. The percentage shares the سود زیان فعلی column, because that is where the
 * card prints it — beside the absolute figure.
 */
const HEADER_COLUMN_BY_CRITERION: Partial<Record<PortfolioSortCriterion, 'netSaleValue' | 'profitLoss'>> = {
	netSaleValue: 'netSaleValue',
	profitLoss: 'profitLoss',
	profitLossPercent: 'profitLoss'
};

const COMPOSITION_COLORS = [
	'#31E8E8',
	'#FFC168',
	'#FF8479',
	'#FF3871',
	'#C63295',
	'#8E43E7',
	'#3369E7',
	'#00AEFF'
] as const;

const MAX_COMPOSITION_ITEMS = 7;

type PortfolioTab = 'portfolio' | 'report';

@Component({
	selector: 'app-portfolio',
	imports: [
		PortfolioCardItemComponent,
		PortfolioCompositionComponent,
		PortfolioMenuBottomSheetComponent,
		PortfolioRiskStatementBottomSheetComponent,
		PortfolioSettingsBottomSheetComponent,
		PortfolioSortBottomSheetComponent,
		PortfolioSummaryComponent,
		UIIconComponent,
		UiTabComponent,
		UiEmptyStateComponent,
		UiLoadingComponent,
		SwipeRightLeftDirective
	],
	templateUrl: './portfolio.component.html',
	// Provided here rather than at the root so the SSE stream is opened while the
	// screen is on and torn down with it, matching how the market list holds it.
	providers: [MarketViewService, PortfolioHeaderEventProducerService, PortfolioHeaderEventConsumerService]
})
export class PortfolioComponent implements OnDestroy {
	private readonly portfolioStore = inject(PortfolioStore);
	private readonly viewState = inject(PortfolioViewStateService);
	private readonly walletStore = inject(WalletStore);
	private readonly marketViewService = inject(MarketViewService);
	private readonly headerProducer = inject(PortfolioHeaderEventProducerService);
	private readonly headerConsumer = inject(PortfolioHeaderEventConsumerService);
	private readonly router = inject(Router);

	private readonly assetList = viewChild<ElementRef<HTMLElement>>('assetList');
	private readonly sortSheet = viewChild<PortfolioSortBottomSheetComponent>('sortSheet');
	private readonly menuSheet = viewChild<PortfolioMenuBottomSheetComponent>('menuSheet');
	private readonly settingsSheet = viewChild<PortfolioSettingsBottomSheetComponent>('settingsSheet');
	private readonly riskStatementSheet = viewChild<PortfolioRiskStatementBottomSheetComponent>('riskStatementSheet');

	/**
	 * The holdings as مبنای قیمت measures them — the store's own revalued list,
	 * not the raw one, so the cards and the sort read the same prices the summary
	 * above them does.
	 */
	readonly assets = this.portfolioStore.valuedAssets;
	/** The summary header's eye toggle; the cards mask their value figures with it. */
	readonly amountsHidden = this.viewState.amountsHidden;
	readonly loading = this.portfolioStore.loading;
	readonly error = this.portfolioStore.error;

	readonly totalNetSaleValue = this.portfolioStore.totalNetSaleValue;
	readonly totalProfitLoss = this.portfolioStore.totalProfitLoss;
	readonly totalProfitLossPercent = this.portfolioStore.totalProfitLossPercent;
	readonly todayProfitLoss = this.portfolioStore.todayProfitLoss;

	/** مانده گزارش = جمع مانده همه کیف‌های کاربر، نه فقط کیف گواهی سپرده. */
	readonly depositBalance = this.walletStore.totalBalance;
	readonly depositBalanceLoaded = this.walletStore.depositLoaded;
	readonly depositBalanceLoading = this.walletStore.loading;
	readonly depositBalanceError = this.walletStore.error;
	/** دارایی کل = ارزش پرتفوی + جمع مانده همه کیف‌ها. */
	readonly totalAssets = computed(() => getTotalAssets(this.assets(), this.depositBalance()));
	readonly maskedValue = MASKED_VALUE;
	readonly activeTab = signal<PortfolioTab>('portfolio');
	readonly assetSummaryExpanded = signal(true);
	readonly tabs: TabItem[] = [
		{ id: 'portfolio', label: 'پرتفوی بورس کالا', dataCy: 'portfolio-tab-portfolio' },
		{ id: 'report', label: 'گزارشات', dataCy: 'portfolio-tab-report' }
	];

	readonly portfolioComposition = computed<PortfolioCompositionItem[]>(() => {
		const grouped = new Map<string, { symbolIsin: string | null; value: number }>();
		for (const asset of this.assets()) {
			const label = asset.symbolShortName?.trim() || asset.symbolName;
			const existing = grouped.get(label);
			grouped.set(label, {
				symbolIsin: existing?.symbolIsin ?? asset.symbolISIN,
				value: (existing?.value ?? 0) + Math.max(0, asset.netSaleValue)
			});
		}

		const ranked = [...grouped.entries()]
			.map(([label, { symbolIsin, value }]) => ({ label, symbolIsin, value }))
			.sort((a, b) => b.value - a.value);
		const items: Array<{
			label: string;
			symbolIsin: string | null;
			value: number;
			isOther: boolean;
			members: Array<{ label: string; symbolIsin: string | null; value: number }>;
		}> =
			ranked.length > MAX_COMPOSITION_ITEMS
				? [
						...ranked
							.slice(0, MAX_COMPOSITION_ITEMS)
							.map(item => ({ ...item, isOther: false, members: [] })),
						{
							label: 'سایر',
							symbolIsin: null,
							value: ranked.slice(MAX_COMPOSITION_ITEMS).reduce((total, item) => total + item.value, 0),
							isOther: true,
							members: ranked.slice(MAX_COMPOSITION_ITEMS).map(item => ({
								...item,
								symbolIsin: item.symbolIsin
							}))
						}
					]
				: ranked.map(item => ({ ...item, isOther: false, members: [] }));
		const totalValue = items.reduce((total, item) => total + item.value, 0);

		return items.map((item, index) => {
			const percentage = totalValue > 0 ? (item.value / totalValue) * 100 : 0;

			return {
				...item,
				percentage,
				color: item.isOther ? '#00aeff' : COMPOSITION_COLORS[index % COMPOSITION_COLORS.length],
				members: item.members.map(member => ({
					...member,
					percentage: totalValue > 0 ? (member.value / totalValue) * 100 : 0
				}))
			};
		});
	});

	readonly showEmptyState = computed(() => !this.loading() && !this.error() && this.assets().length === 0);

	readonly sortCriterion = this.viewState.sortCriterion;
	readonly sortDirection = this.viewState.sortDirection;

	/**
	 * The order the cards are in, as contractIds — the whole of scenario 4.
	 *
	 * The sort is applied at the moments the trader expects a reshuffle (opening
	 * the screen, a refresh, choosing a sort) and the resulting order is held
	 * here until the next one. Sorting `assets()` in a `computed` instead would
	 * re-run on every market tick and pull a card out from under the thumb
	 * reaching for it, which is exactly what the handoff rules out: values move
	 * live, positions do not.
	 */
	private readonly sortedContractIds = signal<readonly number[]>([]);

	/** The holdings in that held order — what the list actually renders. */
	readonly sortedAssets = computed(() => {
		const order = this.sortedContractIds();
		const assets = this.assets();

		if (order.length === 0) {
			return assets;
		}

		const rankByContractId = new Map(order.map((contractId, index) => [contractId, index]));
		// A holding the held order does not name — one that arrived with a reload
		// after the last sort — ranks last, and ties keep their incoming order
		// because `sort` is stable. It is placed properly by the next resort.
		const unranked = order.length;

		return [...assets].sort(
			(a, b) =>
				(rankByContractId.get(a.contractId) ?? unranked) - (rankByContractId.get(b.contractId) ?? unranked)
		);
	});

	/**
	 * Which column header the active criterion lights up, and the icon beside it.
	 *
	 * نام نماد and تعداد دارایی are not among the four figures the header names,
	 * so for those no column is emphasized and the generic sort icon stands in —
	 * the header is still the way into the sheet, which shows the real answer.
	 */
	readonly headerSort = computed(() => {
		const column = HEADER_COLUMN_BY_CRITERION[this.sortCriterion()] ?? null;
		const icon: IconNames = !column ? 'sort' : this.sortDirection() === 'desc' ? 'sort-up' : 'sort-down';

		return { column, icon };
	});

	/** The header button's accessible name: what the list is ordered by, in words. */
	readonly sortSummary = computed(() => {
		const criterion = this.sortCriterion();
		const direction = getSortDirectionLabels(criterion)[this.sortDirection()];

		return `مرتب‌سازی دارایی‌ها، هم‌اکنون بر اساس ${PORTFOLIO_SORT_CRITERION_LABELS[criterion]}، ${direction}`;
	});

	constructor() {
		// Coming back from a holding's detail, the trader should be looking at the
		// row they opened. The store keeps the holdings in memory, so the rebuilt
		// list is the same height and the saved offset still points at that card.
		afterNextRender(() => {
			const element = this.assetList()?.nativeElement;
			if (element) {
				element.scrollTop = this.viewState.listScrollTop();
			}
		});

		// `/portfolios/deposit` carries no prices, so the card's price, change and
		// current-value figures come from the deposit market feed, joined on the
		// contractId both sides report. Subscribing to the whole deposit stream
		// rather than per contract keeps one connection open however many holdings
		// there are, and ticks for symbols not held are simply ignored.
		toSignal(
			this.marketViewService.connectAllDeposits().pipe(
				tap(message =>
					this.portfolioStore.applyPriceUpdate(
						message.ci,
						message.ltp,
						message.lsp,
						// قیمت پایانی is not a field on the tick — it is the session's
						// traded value over its volume. Computed here so مبنای قیمت has
						// it for every holding, not only the one on the detail screen.
						//
						// A tick carrying no volume reports 0 rather than standing in
						// `lsp`: `withPrice` already falls back to the holding's seeded
						// قیمت پایانی — the figure the symbol snapshot gave it — and only reaches
						// for the settlement price when there is no VWAP at all. Passing
						// `lsp` here would beat that fallback and replace today's VWAP
						// with yesterday's close.
						mapMarketViewTickToClosingPrice(message)
					)
				)
			)
		);

		this.headerProducer.configurePortfolioHeader();

		toSignal(
			this.headerConsumer.headerAction$.pipe(
				tap(event => {
					switch (event?.data?.eventId) {
						case PORTFOLIO_MENU_EVENT_ID:
							this.onMenuClick();
							break;
					}
					this.headerProducer.clearEventSnapshot(event.id);
				})
			)
		);

		// Ordered from what the root-provided store still holds before the reload
		// answers, so re-entering the screen paints in the trader's order rather
		// than flashing the API's and then settling.
		this.resort();
		this.reload();
	}

	ngOnDestroy(): void {
		this.headerProducer.resetHeader();
	}

	/** The header's three-dot button: the portfolio's menu, per the handoff's entry path. */
	onMenuClick(): void {
		this.menuSheet()?.open();
	}

	/** تنظیمات پرتفوی, chosen in that menu — where مبنای قیمت is set. */
	onSettingsSelected(): void {
		this.settingsSheet()?.open();
	}

	/**
	 * بیانیه ریسک, chosen in that menu — the statement that the portfolio's
	 * figures are approximate. Dismissing it lands back here, which is what the
	 * screen behind an open sheet already is, so there is nothing to do on the way
	 * back.
	 */
	onRiskStatementSelected(): void {
		this.riskStatementSheet()?.open();
	}

	onRetry(): void {
		this.reload();
	}

	onTabChanged(tab: string): void {
		if (tab === 'report' && !this.depositBalanceLoaded()) {
			this.walletStore.load();
		}
	}

	onReportRetry(): void {
		this.walletStore.load();
	}

	/** The header's sort box — the only way in, per the handoff. */
	onSortClick(): void {
		this.sortSheet()?.open();
	}

	/**
	 * Scenarios 1 and 2: reorder on the trader's choice, and remember it.
	 *
	 * The list is reordered from the emitted choice rather than waiting on the
	 * write, so the cards move as the sheet closes. A failed write leaves the
	 * order the API still holds standing instead.
	 */
	async onSortChanged(sort: { criterion: PortfolioSortCriterion; direction: PortfolioSortDirection }): Promise<void> {
		this.applySort(sort.criterion, sort.direction);

		const persisted = await this.viewState.setSort(sort.criterion, sort.direction);
		if (!persisted) {
			this.resort();
		}
	}

	/**
	 * Reload the holdings, then order them — a refresh resorts, per scenario 4.
	 *
	 * Ordered once at each of the load's two phases. The first is the one the
	 * trader sees: the store drops `loading` as soon as `/portfolios/deposit`
	 * answers, so the list is on screen and interactive while the symbol snapshot is
	 * still in flight, and ordering only after that second call would leave the
	 * cold-load list sitting in raw API order and then visibly reshuffle. The
	 * second phase is what makes the value criteria sortable at all — every
	 * price-derived figure is 0 until the seed lands.
	 */
	private async reload(): Promise<void> {
		const { pricesSeeded } = await this.portfolioStore.loadPortfolio();
		this.resort();

		await pricesSeeded;
		this.resort();
	}

	/** Order the holdings by the sort as it is stored. */
	private resort(): void {
		this.applySort(this.sortCriterion(), this.sortDirection());
	}

	private applySort(criterion: PortfolioSortCriterion, direction: PortfolioSortDirection): void {
		this.sortedContractIds.set(
			sortPortfolioAssets(this.assets(), criterion, direction).map(asset => asset.contractId)
		);
	}

	/** The empty state's only action: go find something to buy. */
	onBrowseMarket(): void {
		this.router.navigate(['/market']);
	}

	/** The three-dot action: the holding's full detail, addressed by contractId. */
	onDetailRequested(asset: PortfolioAsset): void {
		this.router.navigate(['/portfolio', asset.contractId]);
	}

	onListScroll(event: Event): void {
		this.viewState.setListScrollTop((event.target as HTMLElement).scrollTop);
	}
}
